import { Typography } from "@mui/material";
import {
    CARE_PLAN_COMBO_DISCOUNT_ID,
    VALID_CARE_PLANS
} from "@src/constants";
import { cacheUtils } from "@src/index";
import { ui } from "@src/interfaces/interfaces";
import { useAppSelector } from "@src/store/hooks";
import { CacheTypes } from "@src/utilities/cache/interfaces";
import React, { useMemo } from "react";
import { isGasBoilerServiceJob } from "../Layout/BookingForm/MainForm/Steps/StepThree/RatesTab/DiscountsTab/discountsLogic";

/**
 * Handles the logic for applying the Combo Promo to the Care Plan.
 * Updates various UI states and fetches necessary data from the cache.
 */
const ccComboPromo = () => {
    // If a Care Plan reference already exists, do not proceed
    if (ui.CarePlanReference) return;

    // Update UI state to navigate to the Care Plan step and set active tabs
    ui.ActiveStep = 2;
    ui.StepThreeActiveTab = "care plan";
    ui.CarePlanActiveTab = 0;
    ui.CarePlanOffered = true;
    ui.CarePlanPlanDetailsActiveSideTab = 0;

    // Fetch and set the Service Agreement Duration with ID 18 from the cache
    ui.CarePlanPlanDetailsPlanOptionsDuration =
        cacheUtils
            .getCache<CacheTypes.ServiceAgreementDuration>(
                "ServiceAgreementDurations"
            )
            ?.find((p) => p?.serviceAgreementDurationId === 18) ?? undefined;

    // Fetch and set the Service Agreement Billing Frequency with ID 1 from the cache
    ui.CarePlanPlanDetailsPlanOptionsBillingFrequency =
        cacheUtils
            .getCache<CacheTypes.ServiceAgreementBillingFrequency>(
                "ServiceAgreementBillingFrequencies"
            )
            ?.find((p) => p?.serviceAgreementBillingFrequencyId === 1) ?? undefined;

    // Fetch and set the Service Agreement Excess with ID 0 from the cache
    ui.CarePlanPlanDetailsPlanOptionsExcess =
        cacheUtils
            .getCache<CacheTypes.ServiceAgreementExcess>("ServiceAgreementExcesses")
            ?.find((p) => p?.serviceAgreementExcessId === 0) ?? undefined;
};

/**
 * AppliedBadge Component
 * Renders an "APPLIED" badge over the FixedPriceBadge when discounted.
 *
 * @returns {React.FC} The AppliedBadge component.
 */
const AppliedBadge: React.FC = () => (
    <Typography
        variant="caption"
        sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-25deg)",
            bgcolor: "green",
            color: "white",
            fontWeight: "bold",
            fontSize: "1.35rem",
            px: 2,
            py: 0.5,
            zIndex: 1,
            borderRadius: 1,
            opacity: 0.8,
        }}
    >
        APPLIED
    </Typography>
);

/**
 * Props interface for the Circle component.
 */
interface CircleProps {
    /**
     * The number to display inside the circle.
     */
    number: number;
}

/**
 * Circle Component with a number inside + VAT. Should be shown in right corner of the AppliedBadge.
 * 
 * @param {CircleProps} props - The props for the component.
 * @returns {React.FC} The Circle component.
 */
const Circle: React.FC<CircleProps> = ({ number }) => (
    <Typography
        variant="h6"
        sx={{
            display: "flex",
            position: "absolute",
            top: "2%",
            right: "2%",
            justifyContent: "center",
            alignItems: "center",
            width: 50,
            height: 50,
            borderRadius: "50%",
            bgcolor: "green",
            color: "white",
            fontWeight: "bold",
            fontSize: ".9rem",
            textAlign: "center",
        }}
    >
        £{number} +VAT
    </Typography>
);

/**
 * Array of valid Care Plan names used for dynamic rendering.
 */
const PLAN_NAMES = [
    "Homeowner Core",
    "Homeowner Core Plus",
    "Homeowner Ultimate",
];

/**
 * Props interface for the FixedPriceBadge component.
 */
interface FixedPriceBadgeProps {
    /**
     * Indicates whether the badge is in a discounted state.
     */
    discounted: boolean;
}

/**
 * FixedPriceBadge Component
 * Displays a fixed price badge with conditional styling and behavior based on discount status and eligibility.
 *
 * @param {FixedPriceBadgeProps} props - The props for the component.
 * @param {boolean} props.discounted - Indicates if the badge is discounted.
 * @returns {React.FC} The FixedPriceBadge component.
 */
export const FixedPriceBadge: React.FC<FixedPriceBadgeProps> = ({
    discounted,
}) => {
    /**
     * Selectors to retrieve necessary state values from the Redux store.
     */
    const discountId = useAppSelector(({ state }) => state.ChargeRateReductionDiscountId)
    const chargePriceAdj = useAppSelector(({ state }) => state.ChargePriceAdj)
    const duration = useAppSelector(
        (state) => state.ui.CarePlanPlanDetailsPlanOptionsDuration
    );
    const planType = useAppSelector(
        (state) => state.ui.CarePlanPlanDetailsPlanOptionsPlanType
    );
    const jobTypeCode = useAppSelector(({ state }) => state.JobTypeCode);

    /**
     * Determines if the selected duration is 18 months or not selected.
     *
     * @type {boolean}
     */
    const is18MonthsOrNotSelected =
        duration?.serviceAgreementDurationId === 18 || !duration;

    /**
     * Determines if the selected plan type is valid or not selected.
     *
     * @type {boolean}
     */
    const isEligibleOrNotSelected =
        VALID_CARE_PLANS.includes(planType?.serviceAgreementTypeId ?? 0) ||
        !planType;

    /**
     * Checks if the current discount ID matches the Combo Promo discount ID.
     *
     * @type {boolean}
     */
    const isComboPromo =
        discountId > 0 && discountId === CARE_PLAN_COMBO_DISCOUNT_ID;

    /**
     * Determines if the job type is Gas Boiler Service.
     */
    const isJobTypeGasBoilerService = useMemo(() => {
        return isGasBoilerServiceJob(jobTypeCode);
    }, [jobTypeCode]);


    /**
     * Handles the click event on the FixedPriceBadge.
     * Applies the Combo Promo if not in a discounted state.
     */
    const handleClick = () => {
        if (!discounted) {
            ccComboPromo();
        }
    };

    /**
     * Computes the fixed price based on promo eligibility and discount status.
     * Memoized to optimize performance by avoiding unnecessary recalculations.
     *
     * @type {number | string}
     */
    const fixedPrice = useMemo(() => {
        if (
            (isComboPromo || !discounted) &&
            is18MonthsOrNotSelected &&
            isEligibleOrNotSelected &&
            isJobTypeGasBoilerService
        ) {
            return 69;
        }
        if (!discounted) {
            return (chargePriceAdj * 0.9).toFixed(2);
        }
        return chargePriceAdj;
    }, [
        isComboPromo,
        discounted,
        is18MonthsOrNotSelected,
        isEligibleOrNotSelected,
        chargePriceAdj,
        isJobTypeGasBoilerService,
    ]);

    /**
     * Determines the description text based on promo eligibility and discount status.
     * Memoized to optimize performance by avoiding unnecessary recalculations.
     *
     * @type {React.ReactNode | string}
     */
    const description = useMemo(() => {
        if (
            (!isComboPromo && discounted) ||
            (!is18MonthsOrNotSelected && !discounted) ||
            (!isEligibleOrNotSelected && !discounted) ||
            !isJobTypeGasBoilerService
        ) {
            return "Discounted Price if CC Plan Sold!";
        }

        return (
            <>
                <Typography
                    variant="body2"
                    component="div"
                    sx={{ mt: 0.5, fontStyle: "italic" }}
                >
                    If sold 18 months CC Plan:
                </Typography>
                {PLAN_NAMES.map((name) => (
                    <Typography
                        key={name}
                        variant="body2"
                        component="div"
                        sx={{ mt: 0.5, fontStyle: "italic" }}
                    >
                        - {name}
                    </Typography>
                ))}
            </>
        );
    }, [isComboPromo, discounted, is18MonthsOrNotSelected, isEligibleOrNotSelected, isJobTypeGasBoilerService]);

    return (
        <Typography
            variant="h6"
            sx={{
                ml: 1,
                mr: 1,
                p: 1,
                borderRadius: 2.5,
                border: 1,
                borderColor: discounted ? "gray" : "green",
                borderStyle: "solid",
                bgcolor: discounted ? "#e0e0e0" : "#ffc8006e",
                cursor: discounted ? "not-allowed" : "pointer",
                position: "relative",
                opacity: discounted ? 0.6 : 1,
            }}
            onClick={handleClick}
        >
            {/* Render the "APPLIED" badge if the badge is in a discounted state */}
            {discounted && <AppliedBadge />}
            {discounted && <Circle number={Number(fixedPrice)} />}

            {/* Display the fixed price with VAT */}
            <strong>
                Fixed Price £{fixedPrice} + VAT
            </strong>

            {/* Display the description based on eligibility and discount status */}
            <Typography
                variant="body2"
                component="div"
                sx={{ mt: 0.5, fontStyle: "italic" }}
            >
                {description}
            </Typography>

            {/* If not discounted, provide a prompt to prefill CC plan options */}
            {!discounted && (
                <Typography
                    variant="body2"
                    component="div"
                    sx={{ mt: 0.5, fontStyle: "italic" }}
                >
                    Click here to prefill CC plan options!
                </Typography>
            )}
        </Typography>
    );
};
